import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@easyflow/ui-master/components/ui/dialog';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { Loader2, Phone, Users as UsersIcon, Bot, SlidersHorizontal } from 'lucide-react';
import type {
  TenantLimitsResponse,
  TenantLimitSection,
  TenantLimitStatus,
  VoiceChannelCounterState,
} from '@easyflow/shared';

interface TenantLimitsModalProps {
  open: boolean;
  onClose: () => void;
  tenant: {
    id: string;
    name: string;
  } | null;
}

const STATUS_STYLES: Record<TenantLimitStatus, {
  bg: string;
  text: string;
  bar: string;
  label: string;
}> = {
  ok: {
    bg: 'bg-emerald-500/10 border-emerald-500/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    bar: 'bg-emerald-500',
    label: 'OK',
  },
  warning: {
    bg: 'bg-amber-500/10 border-amber-500/30',
    text: 'text-amber-700 dark:text-amber-400',
    bar: 'bg-amber-500',
    label: 'Vicino al limite',
  },
  full: {
    bg: 'bg-red-500/10 border-red-500/30',
    text: 'text-red-700 dark:text-red-400',
    bar: 'bg-red-500',
    label: 'Limite raggiunto',
  },
};

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  section: TenantLimitSection;
  counter?: VoiceChannelCounterState;
  inputId: string;
  inputValue: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  helperText: string;
  presets: number[];
}

function SectionCard({
  icon,
  title,
  description,
  section,
  counter,
  inputId,
  inputValue,
  onChange,
  min,
  max,
  helperText,
  presets,
}: SectionCardProps) {
  const verified = !counter || counter.state === 'verified';
  const styles = STATUS_STYLES[verified ? section.status : 'warning'];
  const pct = section.limit > 0
    ? Math.min(100, Math.round((section.current / section.limit) * 100))
    : 100;

  return (
    <div className="space-y-3 border rounded-lg p-4">
      <div className={`p-3 rounded-md border ${styles.bg}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className={styles.text}>{icon}</span>
            <div>
              <p className="text-sm font-semibold text-foreground">{title}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-xl font-bold ${styles.text}`}>
              {verified ? section.current : '—'} / {section.limit}
            </div>
            <div className={`text-xs ${verified ? styles.text : 'text-amber-700 dark:text-amber-400'}`}>
              {verified ? styles.label : 'Conteggio non verificato'}
            </div>
          </div>
        </div>
        {verified && <div
          className="h-2 w-full overflow-hidden rounded-full bg-background/60"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label={`${title}: ${section.current} su ${section.limit} (${pct}%)`}
        >
          <div
            className={`h-full ${styles.bar} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>}
        {counter && (
          <p className={`text-xs mt-2 ${verified ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'}`}>
            {counter.scope === 'cluster' ? 'Condiviso tra nodi' : 'Locale a questo processo'}
            {' · '}{verified ? 'Verificato' : 'Degradato'}
            {counter.reason && ` · ${counter.reason.replace(/_/g, ' ')}`}
            {!verified && ' · Utilizzo globale non verificabile'}
          </p>
        )}
        {verified && <div className="flex justify-between text-[10px] mt-1 text-muted-foreground">
          <span>0</span>
          <span>{pct}%</span>
          <span>{section.limit}</span>
        </div>}
      </div>

      <div className="space-y-2">
        <Label htmlFor={inputId}>Limite massimo</Label>
        <Input
          id={inputId}
          type="number"
          min={min}
          max={max}
          value={inputValue}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || min)}
        />
        <p className="text-xs text-muted-foreground">{helperText}</p>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {presets.map((preset) => (
          <Button
            key={preset}
            variant={inputValue === preset ? 'default' : 'outline'}
            size="sm"
            type="button"
            onClick={() => onChange(preset)}
          >
            {preset}
          </Button>
        ))}
      </div>
    </div>
  );
}

const ZERO_SECTION: TenantLimitSection = { current: 0, limit: 0, status: 'ok' };

export function TenantLimitsModal({ open, onClose, tenant }: TenantLimitsModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [voipSection, setVoipSection] = useState<TenantLimitsResponse['voip'] | null>(null);
  const [usersSection, setUsersSection] = useState<TenantLimitSection>(ZERO_SECTION);
  const [agentsSection, setAgentsSection] = useState<TenantLimitSection>(ZERO_SECTION);
  const [voipLimit, setVoipLimit] = useState(20);
  const [usersLimit, setUsersLimit] = useState(50);
  const [agentsLimit, setAgentsLimit] = useState(10);

  const applyData = useCallback((data: TenantLimitsResponse) => {
    setVoipSection(data.voip);
    setUsersSection(data.users);
    setAgentsSection(data.ai_agents);
    setVoipLimit(data.voip.limit);
    setUsersLimit(data.users.limit);
    setAgentsLimit(data.ai_agents.limit);
  }, []);

  const loadLimits = useCallback(async () => {
    if (!tenant) return;
    setVoipSection(null);
    setLoading(true);
    try {
      const response = await apiClient.get<TenantLimitsResponse>(`/master/tenants/${tenant.id}/limits`);
      if (!response.data) throw new Error('Risposta limiti non disponibile');
      applyData(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore caricamento limiti';
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tenant, toast, applyData]);

  useEffect(() => {
    if (open && tenant) void loadLimits();
  }, [open, tenant, loadLimits]);

  const handleSave = async () => {
    if (!tenant) return;
    setSaving(true);
    try {
      const response = await apiClient.put<TenantLimitsResponse>(
        `/master/tenants/${tenant.id}/limits`,
        {
          max_concurrent_calls: voipLimit,
          max_users: usersLimit,
          max_ai_agents: agentsLimit,
        },
      );
      if (response?.data) applyData(response.data);
      toast({ title: 'Limiti aggiornati', description: `Tenant "${tenant.name}" aggiornato.` });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore aggiornamento limiti';
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-primary" />
            Limiti tenant
          </DialogTitle>
          <DialogDescription>
            Configura i limiti operativi per <strong>{tenant?.name}</strong>: chiamate concorrenti, utenti e agenti AI.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : voipSection ? (
          <div className="space-y-4 py-2">
            <SectionCard
              icon={<Phone className="h-5 w-5" />}
              title="Chiamate VoIP simultanee"
              description="Canali concorrenti gestiti dal motore FreeSWITCH"
              section={voipSection}
              counter={voipSection.counter}
              inputId="voipLimit"
              inputValue={voipLimit}
              onChange={setVoipLimit}
              min={1}
              max={1000}
              helperText="Al raggiungimento del limite le nuove chiamate vengono rifiutate (busy tone)."
              presets={[5, 20, 50, 100]}
            />

            <SectionCard
              icon={<UsersIcon className="h-5 w-5" />}
              title="Utenti del tenant"
              description="Account dipendente/operatore creabili"
              section={usersSection}
              inputId="usersLimit"
              inputValue={usersLimit}
              onChange={setUsersLimit}
              min={1}
              max={10000}
              helperText="Nuove creazioni utente sopra il limite vengono rifiutate (HTTP 403)."
              presets={[10, 50, 100, 500]}
            />

            <SectionCard
              icon={<Bot className="h-5 w-5" />}
              title="Agenti AI (totale)"
              description="Voice + Chatbot + Personal Assistant + ElevenLabs"
              section={agentsSection}
              inputId="agentsLimit"
              inputValue={agentsLimit}
              onChange={setAgentsLimit}
              min={1}
              max={1000}
              helperText="Aggregato su tutti i tipi di agente AI configurati per il tenant."
              presets={[5, 10, 25, 50]}
            />
          </div>
        ) : (
          <p className="py-4 text-sm text-muted-foreground">Limiti non disponibili. Riapri per riprovare.</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => { void handleSave(); }} disabled={saving || loading || !voipSection}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salva limiti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
